import _, { size, chain, every, noop, get, isEmpty, sortBy } from 'underscore';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { ValidationGroup, withCustomValidation, withError } from '@bingads-webui-react/validation';
import { ValidatedAudiencePicker } from '@bingads-webui-campaign-react/xandr-campaign';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { withBidAdjustment } from './with-bid-adjustment';
import { CampaignObjectiveType } from '../../../common/constants';

const ValidatedAudiencePickerWithAudienceInfo = (props) => {
  const {
    shouldHide, onAudienceTargetInfoChange, updateValidation, validationProps, isAudienceCreationWorkflow, campaignGoal, boostConversionBasedSegmentEnabled, hideCustomSegmentsOption, ...rest
  } = props;
  const { data: { audiences } } = validationProps;

  const currentLevelTargetInfo = useMemo(() => (
    new AudienceTargetInfo({
      resourceName: 'AdGroupCriterion',
      isForVNext: true,
      initAudienceAssociationView: false,
    })), []);

  const getAudienceTargetErrors = (audienceList) => {
    const errors = [];
    const customerMatchList = chain(audienceList)
      .filter(i => _.result(_.result(i, 'Criterion'), 'AudienceType') === 'CustomerList')
      .value();
    const excludeCustomerMatchList = _.filter(customerMatchList, i => i.Status === 'Excluded');
    const includeCustomerMatchList = _.filter(customerMatchList, i => i.Status === 'Active');

    if (campaignGoal === CampaignObjectiveType.Acquisition && boostConversionBasedSegmentEnabled &&
      size(excludeCustomerMatchList) === 0) {
      errors.push(_TL_('Need at least one exclusion customer match list when your campaign goal is Acquisition.'));
    }

    if (campaignGoal === CampaignObjectiveType.Engagement && boostConversionBasedSegmentEnabled &&
      size(includeCustomerMatchList) === 0) {
      errors.push(_TL_('Need at least one inclusion customer match list when your campaign goal is Engagement.'));
    }
    return errors;
  };

  const onChange = () => {
    const updatedItems = currentLevelTargetInfo.getSnapshot(false);
    const errors = getAudienceTargetErrors(updatedItems);
    updateValidation(errors, { audiences: [...updatedItems] });
  };

  const getPreExistingBids = (audience, currentTargets) => {
    const { Criterion = {} } = audience;

    if (Criterion.BidMultiplier > 0) {
      return Criterion.BidMultiplier;
    }

    const existingAudience = currentTargets.find(({ Criterion: criterion }) => (criterion.AudienceId === get(audience, 'Criterion.AudienceId')));

    if (existingAudience) {
      return {
        Id: existingAudience.Id,
        BidMultiplier: existingAudience.Criterion.BidMultiplier,
      };
    }

    return {
      BidMultiplier: Criterion.BidMultiplier,
    };
  };

  const onValidate = (result) => {
    const { data } = result;
    if (!isEmpty(data)) {
      const currentTargets = currentLevelTargetInfo.getSnapshot(false);
      // add the existing bids and Ids to audiences because ValidatedAudiencePicker removes it
      const hydratedAudiences = data.audiences.map((audience) => {
        const { Criterion = {} } = audience;
        const { Id, BidMultiplier } = getPreExistingBids(audience, currentTargets);

        const newAudience = {
          ...audience,
          Id,
          Criterion: {
            ...Criterion,
            BidMultiplier,
          },

        };

        return newAudience;
      });

      updateValidation(getAudienceTargetErrors(hydratedAudiences), { audiences: hydratedAudiences });
    }

    if (isAudienceCreationWorkflow && isEmpty(data)) {
      updateValidation(getAudienceTargetErrors([]), { audiences: [] });
    }
  };

  useEffect(() => {
    if (onAudienceTargetInfoChange) {
      onAudienceTargetInfoChange(currentLevelTargetInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLevelTargetInfo]);

  useEffect(() => {
    if (currentLevelTargetInfo && audiences) {
      currentLevelTargetInfo.off('AdGroupCriterionAudienceChanged', onChange);
      const currentTargets = currentLevelTargetInfo.getSnapshot(false);

      const isSame = every(audiences, audience => currentTargets.find(({ Criterion }) => (Criterion.AudienceId === get(audience, 'Criterion.AudienceId'))));

      if ((audiences.length !== currentTargets.length) || !isSame) {
        currentLevelTargetInfo.destroyTargetTypeAsync('Audience').then(() => {
          const sortedAudiences = sortBy(audiences, 'Status');
          sortedAudiences.forEach((audience) => {
            const { Criterion = {} } = audience;
            const newCriterion = {
              ...audience,
              Criterion: {
                ...Criterion,
              },
            };

            currentLevelTargetInfo.createAsync(newCriterion);
          });
        }).then(() => {
          currentLevelTargetInfo.on('AdGroupCriterionAudienceChanged', onChange);
        });
      } else {
        currentLevelTargetInfo.on('AdGroupCriterionAudienceChanged', onChange);
      }
    }

    return () => {
      currentLevelTargetInfo.off('AdGroupCriterionAudienceChanged', onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiences]);

  if (shouldHide) {
    return null;
  }

  return (
    <>
      <ValidationGroup
        validationProps={{
          data: audiences,
          onValidate,
          stopPropagation: true,
        }}
      >
        <ValidatedAudiencePicker {...rest} validationProps={{ data: { audiences } }} hideCustomSegmentsOption={hideCustomSegmentsOption} />
      </ValidationGroup>
    </>
  );
};

ValidatedAudiencePickerWithAudienceInfo.propTypes = {
  ...ValidatedAudiencePicker.propTypes,
  shouldHide: PropTypes.bool,
  onAudienceTargetInfoChange: PropTypes.func,
  isAudienceCreationWorkflow: PropTypes.bool,
  hideCustomSegmentsOption: PropTypes.bool,
};

ValidatedAudiencePickerWithAudienceInfo.defaultProps = {
  shouldHide: false,
  onAudienceTargetInfoChange: noop,
  isAudienceCreationWorkflow: false,
  hideCustomSegmentsOption: false,
};

export const ValidatedAudiencePickerWithBidAdjustment = withBidAdjustment(withError(withCustomValidation(ValidatedAudiencePickerWithAudienceInfo)), 'Audience', ['audiences']);

